/**
 * Error normalizado de la capa HTTP.
 *
 * Toda petición fallida termina en una instancia de `ApiError`, de modo que la
 * UI nunca tiene que inspeccionar `Response` ni códigos sueltos.
 */

export interface ApiErrorInit {
  status?: number;
  code?: string;
  details?: unknown;
  cause?: unknown;
}

export class ApiError extends Error {
  /** Código HTTP (0 cuando la request no llegó al servidor). */
  readonly status: number;

  /** Código de negocio devuelto por la API (ej. `INVALID_CREDENTIALS`). */
  readonly code: string;

  /** Payload original, útil para depuración o para mostrar detalles. */
  readonly details?: unknown;

  constructor(message: string, init: ApiErrorInit = {}) {
    super(message);

    this.name = 'ApiError';
    this.status = init.status ?? 0;
    this.code = init.code ?? 'UNKNOWN';
    this.details = init.details;

    if (init.cause !== undefined) {
      this.cause = init.cause;
    }
  }

  /** La request no llegó al servidor (red caída, CORS, timeout). */
  get isNetworkError(): boolean {
    return this.status === 0;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }

  get isServerError(): boolean {
    return this.status >= 500;
  }
}

/** Type guard sin dependencias de `instanceof` entre bundles. */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Mensaje apto para mostrar al usuario final.
 * Prefiere el mensaje del backend y cae en un texto genérico por estado.
 */
export function toUserMessage(error: unknown): string {
  if (isApiError(error)) {
    if (error.isNetworkError) {
      return 'No pudimos conectar con el servidor. Verifica tu conexión e inténtalo otra vez.';
    }

    if (error.isServerError) {
      return 'Ocurrió un error en el servidor. Intenta nuevamente en unos minutos.';
    }

    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Ocurrió un error inesperado.';
}
